import { useState } from "react";
import { toast } from "sonner";
import { Share2, Link as LinkIcon, Twitter, Facebook, Mail, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

/**
 * ShareButton — opens a dialog with the canonical /api/share/{type}/{id} URL,
 * which serves social-media-crawlable HTML with OG/Twitter meta tags + auto-redirect
 * to the SPA route. Humans clicking the link get redirected to the SPA;
 * bots (Facebook, Twitter, LinkedIn, Slack) read the rich preview.
 *
 * Phase B — co-branded share links: when the signed-in user has a `referral_code`,
 * we automatically append `?ref={code}` so the share preview attributes the listing
 * to the agent ("Listed by Alvin Kumar ✓ Verified") and any sign-up from that link
 * tags the agent as the referrer.
 */
export default function ShareButton({ type, id, title = "Share", className = "", variant = "outline", size = "default" }) {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const { user } = useAuth();

    const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    const refCode = user?.referral_code || "";
    const refQuery = refCode ? `?ref=${encodeURIComponent(refCode)}` : "";
    const shareUrl = `${apiBase}/api/share/${type}/${id}${refQuery}`;
    const shareText = `${title} — Bula Real Estate Fiji${refCode ? ` · shared by ${user?.full_name || "an agent"}` : ""}`;

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            toast.success("Link copied");
            setTimeout(() => setCopied(false), 2200);
        } catch (e) {
            toast.error("Could not copy");
        }
    };

    const tryNative = async () => {
        if (typeof navigator !== "undefined" && navigator.share) {
            try {
                await navigator.share({ title: shareText, url: shareUrl });
                return true;
            } catch (e) {
                // user cancelled or unsupported — fall through to dialog
            }
        }
        return false;
    };

    const handleClick = async () => {
        const used = await tryNative();
        if (!used) setOpen(true);
    };

    return (
        <>
            <Button
                type="button"
                onClick={handleClick}
                variant={variant}
                size={size}
                data-testid="share-btn"
                className={`font-app ${className}`}
            >
                <Share2 className="h-4 w-4 mr-2" /> Share
            </Button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="share-dialog" className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display">Share this {type === "jv" ? "Joint Venture" : type}</DialogTitle>
                        <DialogDescription className="font-app">
                            Copy the link or post directly — every preview includes a Fiji-branded image, title, and summary.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="flex gap-2 items-center bg-secondary/40 rounded-lg p-2 pl-3 text-sm font-app">
                            <LinkIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                            <div className="flex-1 truncate text-xs">{shareUrl}</div>
                            <Button
                                type="button"
                                size="sm"
                                onClick={copyLink}
                                data-testid="share-copy-btn"
                                className="bg-ocean-green text-white shrink-0"
                            >
                                {copied ? <Check className="h-3.5 w-3.5" /> : "Copy"}
                            </Button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <a
                                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="share-twitter"
                                className="flex flex-col items-center gap-1 py-3 rounded-lg border border-border hover:bg-secondary/40 transition"
                            >
                                <Twitter className="h-5 w-5 text-coral" />
                                <span className="text-xs font-app">Twitter</span>
                            </a>
                            <a
                                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="share-facebook"
                                className="flex flex-col items-center gap-1 py-3 rounded-lg border border-border hover:bg-secondary/40 transition"
                            >
                                <Facebook className="h-5 w-5 text-ocean" />
                                <span className="text-xs font-app">Facebook</span>
                            </a>
                            <a
                                href={`mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent("Have a look at this:\n\n" + shareUrl)}`}
                                data-testid="share-email"
                                className="flex flex-col items-center gap-1 py-3 rounded-lg border border-border hover:bg-secondary/40 transition"
                            >
                                <Mail className="h-5 w-5 text-fiji-green" />
                                <span className="text-xs font-app">Email</span>
                            </a>
                        </div>
                        <p className="text-xs text-muted-foreground font-app text-center">
                            Each share link includes a rich preview (image, title, summary) on Facebook, LinkedIn, Slack, WhatsApp & X.
                        </p>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
